/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import React, { useState } from 'react';
import { TableListHeader } from '../../_shared/table-list';
import { DatePicker, message } from 'antd';
import { Button } from '../../_shared/button';
import { Select } from '../../_shared/select';
import { generateTechDataReport } from '../../_shared/services/analytics.service';
import { useSelector, useStore } from 'react-redux';
import { AppState } from '../../app.state';
import styles from './styles';
import { get, uniq, keyBy, size } from 'lodash';
import moment from 'moment';
import { RangePickerValue } from 'antd/lib/date-picker/interface';
import { Location } from '../../_shared/interfaces/location';
import { CONTENT } from '../../utils/publish';
import { sortByLabel } from '../../utils';
import { Loading } from '../../_shared/icon';
import { Column } from '../../_shared/row';
import defaultStyles from '../../_shared/styles';

export const AlertEvents = (props = {}) => {
  const store = useStore();
  const [orgs] = useState<{ [k: string]: any }>(keyBy(get(store.getState(), 'dash.orgs'), 'sk'));
  const locations = useSelector((state: AppState) => state.dash.locations);
  const user = useSelector(s => get(s, 'auth.user'));

  const [state, setState] = useState({
    loading: false,
    data: {} as { url?: string },
    orgs: [] as string[],
    locations: [] as string[],
    alertNames: [] as string[],
    date: {
      selected: '1_month',
      start: moment().subtract(1, 'month'),
      end: moment(),
    },
    filters: [
      {
        id: 'orgs',
        opts: Object.keys(orgs).map(k => ({
          label: orgs[k].name,
          value: orgs[k].sk,
        })).sort(sortByLabel),
        props: {
          placeholder: 'Select Organizations',
        },
      },
      {
        id: 'locations',
        opts: locations.map((role: Location) => ({
          label: role.name,
          value: role.sk,
        })).sort(sortByLabel),
        props: {
          placeholder: 'Select Locations',
        },
      },
      {
        id: 'alertNames',
        opts: (() => {
          const {
            compressorErrorText,
            vacuumErrorText,
            handpieceErrorText,
            chairErrorText,
            maintErrorText,
          } = CONTENT;

          const names: string[] = [];

          const loop = (obj = {}) => {
            Object.keys(obj).map(k => {
              const error = get(obj, [k, 'error'], {});

              Object.keys(error).map(ek => {
                const alertName = get(error, [ek, 'alertName']);

                if (alertName && ek !== '0') {
                  names.push(alertName);
                }
              });
            });
          };

          loop(compressorErrorText);
          loop(vacuumErrorText);
          loop(handpieceErrorText);
          loop(chairErrorText);
          loop(maintErrorText);

          return uniq(names).map(n => ({ label: n, value: n })).sort(sortByLabel);
        })(),
        props: {
          placeholder: 'Select Alert Names',
        },
      },
    ],
  });

  const retrieveData = async () => {
    try {
      setState(s => ({ ...s, loading: true, data: {} }));

      const OrgKeys = (() => {
        const obj = {
          serviceOrgs: [] as string[],
          dentalOrgs: [] as string[],
        };

        state.orgs.map(o => {
          const org = get(orgs, o);
          const oType = get(org, 'orgType');
          if (oType !== undefined) {
            obj[oType == 1 ? 'dentalOrgs' : 'serviceOrgs'].push(o);
          }
        });

        return obj;
      })();

      const data = await generateTechDataReport(
        {
          user,
          ...OrgKeys,
          date: state.date,
          locations: state.locations,
          alertNames: state.alertNames,
        },
        'AlertEventsData'
      );

      setState(s => ({ ...s, data: data || {} }));
    } catch (err) {
      const msg = err.message;
      if ((msg || '').toLowerCase().indexOf('network error') > -1) {
        message.info(
          'The network request has timed out, but your report is still being generated and will be sent to your email.'
        );
      } else {
        message.error(`Failed to generate report: ${err.message}`);
      }
    } finally {
      setState(s => ({ ...s, loading: false }));
    }
  };

  const changeSelect = (filt: string, values: string[]) => {
    setState(s => ({ ...s, [filt]: values, data: {} }));
  };

  // #region date stuff
  const dateRangeChange = (
    _pickerVals: RangePickerValue,
    dates: [string, string]
  ) => {
    const date = { ...state.date };
    date.start = moment(dates[0]).startOf('day');
    date.end = moment(dates[1]).endOf('day');
    setState(s => ({ ...s, date, data: {} }));
  };

  const datePickerChange = (date: moment.Moment) => {
    const _date = { ...state.date };
    _date.start = moment(date).startOf('day');
    _date.end = _date.start.clone().endOf('day');
    setState(s => ({ ...s, date: _date, data: {} }));
  };

  const dateChange = (val: string) => {
    const date = { ...state.date };
    if (val === '1_day') {
      date.start = moment().subtract(1, 'day');
    } else if (val === '1_week') {
      date.start = moment().subtract(1, 'week');
    } else if (val === '1_month') {
      date.start = moment().subtract(1, 'month');
    } else if (val === '3_month') {
      date.start = moment().subtract(3, 'month');
    } else if (val === '6_month') {
      date.start = moment().subtract(6, 'month');
    }
    date.end = moment();
    date.selected = val;
    setState(s => ({ ...s, date, data: {} }));
  };

  const renderDateDrop = () => {
    const {
      date: { selected, start, end },
    } = state;

    const options = [
      { title: '1 day', value: '1_day' },
      { title: '1 week', value: '1_week' },
      { title: '1 month', value: '1_month' },
      { title: '3 months', value: '3_month' },
      { title: '6 months', value: '6_month' },
      { title: 'Custom', value: 'custom_date' },
    ];

    return (
      <div>
        <Select onChange={v => dateChange(v as string)} defaultValue={selected}>
          {options.map((opt, i) => (
            <Select.Option value={opt.value} key={i}>
              {opt.title}
            </Select.Option>
          ))}
        </Select>
        {selected === 'custom_date' && (
          <DatePicker.RangePicker
            onChange={dateRangeChange as any}
            defaultValue={[start, end] as any}
          />
        )}
        {selected === '1_day' && (
          <DatePicker onChange={datePickerChange as any} defaultValue={start as any} />
        )}
      </div>
    );
  };
  //#endregion

  const { loading, data } = state;

  // At least an Organization or an Alert Name is required before the report can be run - an
  // unfiltered run covers the entire fleet. Unlike the other reports these deps are an OR, and
  // Locations deliberately does not satisfy the requirement on its own.
  const deps = ['orgs', 'alertNames'];

  const canRun = size(deps.filter(d => size(get(state, d)))) > 0;

  return (
    <div style={{ height: '100%' }}>
      <TableListHeader style={`justify-content: flex-start;`}>
        {state.filters.map((filt: any) => {
          return (
            <Select
              key={filt.id}
              mode="multiple"
              css={css(styles.select)}
              onChange={value => changeSelect(filt.id, value as string[])}
              filterOption={(input, option) => {
                const name = option!.props.children as string;
                if (name)
                  return name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                return true;
              }}
              {...(filt.props || {})}
            >
              {(get(filt, 'opts', []) as never[]).map(
                (item: { label: string; value: string | number }) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                )
              )}
            </Select>
          );
        })}
      </TableListHeader>

      <TableListHeader
        style={`justify-content: flex-start; align-items: center;`}
      >
        {renderDateDrop()}
        <Button
          css={css('margin-left: auto;')}
          loading={loading}
          title="Run Report"
          disabled={!canRun}
          onClick={retrieveData}
        />
      </TableListHeader>

      <Column center fullHeight>
        {loading ? (
          <>
            <Loading />
            <p css={css(styles.generateText)}>
              Generating your report. You will receive an email when it
              finishes.
            </p>
          </>
        ) : (
          <>
            <p css={css(defaultStyles.header)}>Alert Events Report</p>
            <p css={css(styles.generateText)}>
              Use the filters above to define the query, and then select 'Run
              Report'. The results will be emailed to you within a few minutes.
            </p>
            {!!get(data, 'url') && (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <a href={get(data, 'url')} target="_blank">
                  Download Report
                </a>
              </div>
            )}
          </>
        )}
      </Column>
    </div>
  );
};
